import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { novelCategoriesApi } from '../../services/api';

// Async thunks
export const fetchNovelCategories = createAsyncThunk(
    'novelCategories/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await novelCategoriesApi.getAll();
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch novel categories');
        }
    }
);

export const createNovelCategory = createAsyncThunk(
    'novelCategories/create',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await novelCategoriesApi.create(formData);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create novel category');
        }
    }
);

export const updateNovelCategory = createAsyncThunk(
    'novelCategories/update',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await novelCategoriesApi.update(id, formData);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update novel category');
        }
    }
);

export const deleteNovelCategory = createAsyncThunk(
    'novelCategories/delete',
    async (id, { rejectWithValue }) => {
        try {
            await novelCategoriesApi.delete(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete novel category');
        }
    }
);

export const updateNovelCategoryOrder = createAsyncThunk(
    'novelCategories/updateOrder',
    async (order, { rejectWithValue }) => {
        try {
            await novelCategoriesApi.updateOrder(order);
            return order;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update order');
        }
    }
);

export const toggleNovelCategoryVisibility = createAsyncThunk(
    'novelCategories/toggleVisibility',
    async ({ id, is_hidden }, { rejectWithValue }) => {
        try {
            await novelCategoriesApi.toggleVisibility(id, is_hidden);
            return { id, is_hidden };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to toggle visibility');
        }
    }
);


const initialState = {
    items: [],
    loading: false,
    error: null,
    selectedCategory: null,
};

const novelCategoriesSlice = createSlice({
    name: 'novelCategories',
    initialState,
    reducers: {
        setSelectedNovelCategory: (state, action) => {
            state.selectedCategory = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        reorderNovelCategories: (state, action) => {
            state.items = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNovelCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNovelCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload || [];
            })
            .addCase(fetchNovelCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createNovelCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(createNovelCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.items.push(action.payload);
            })
            .addCase(createNovelCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateNovelCategory.fulfilled, (state, action) => {
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = { ...state.items[index], ...action.payload };
                }
            })
            .addCase(deleteNovelCategory.fulfilled, (state, action) => {
                state.items = state.items.filter(item => item.id !== action.payload);
            })
            // Add this right here!
            .addCase(toggleNovelCategoryVisibility.fulfilled, (state, action) => {
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index].is_hidden = action.payload.is_hidden ? 1 : 0;
                }
            });
    },
});

export const { setSelectedNovelCategory, clearError, reorderNovelCategories } = novelCategoriesSlice.actions;
export default novelCategoriesSlice.reducer;
